import { X, Loader2 } from 'lucide-react';

interface TestResult {
  formFieldLabel: string;
  crmFieldLabel: string;
  isValid: boolean;
  error?: string;
}

interface FieldMappingTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: TestResult[];
  totalMappings: number;
  validMappings: number;
  inProgress?: boolean;
  saveCompleted?: boolean;
}

/**
 * Modal component that displays the results of field mapping tests
 */
const FieldMappingTestModal = ({
  isOpen,
  onClose,
  results,
  totalMappings,
  validMappings,
  inProgress = false,
  saveCompleted = false,
}: FieldMappingTestModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Field Mapping Test Results</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Summary Banner */}
        <div
          className={`border rounded-md p-4 mb-6 flex justify-between items-center ${
            saveCompleted && validMappings === totalMappings && totalMappings > 0
              ? 'bg-green-50 border-green-100'
              : 'bg-blue-50 border-blue-100'
          }`}
        >
          <p
            className={`text-lg ${
              saveCompleted && validMappings === totalMappings && totalMappings > 0
                ? 'text-green-800'
                : 'text-blue-800'
            }`}
          >
            {inProgress
              ? `Testing ${results.length} of ${totalMappings} mappings...`
              : saveCompleted && validMappings === totalMappings && totalMappings > 0
                ? `All ${totalMappings} mappings validated and saved successfully!`
                : `Validated ${totalMappings} mappings: ${validMappings} valid, ${totalMappings - validMappings} invalid`}
          </p>
          {inProgress && <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />}
        </div>

        {/* Results List */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Results:</h3>
          <div className="max-h-96 overflow-y-auto">
            {results.length === 0 && inProgress && (
              <div className="flex justify-center items-center p-8 text-gray-500">
                <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                <p>Testing field mappings...</p>
              </div>
            )}

            {results.length === 0 && !inProgress && (
              <div className="p-4 text-center text-gray-500">
                <p>No results to display yet</p>
              </div>
            )}

            {results.map((result, index) => (
              <div
                key={index}
                className={`p-4 rounded-md mb-2 ${
                  result.isValid
                    ? 'bg-green-50 border border-green-100'
                    : 'bg-red-50 border border-red-100'
                }`}
              >
                <div className="flex items-start">
                  <div
                    className={`mt-1 mr-3 ${result.isValid ? 'text-green-500' : 'text-red-500'}`}
                  >
                    {result.isValid ? '✓' : '✗'}
                  </div>
                  <div>
                    <p className="text-gray-900">
                      {result.formFieldLabel} → {result.crmFieldLabel}
                    </p>
                    {!result.isValid && result.error && (
                      <p className="text-red-600 text-sm mt-1">{result.error}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          {inProgress ? (
            <button
              disabled
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md cursor-not-allowed opacity-50"
            >
              Testing...
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldMappingTestModal;
